import * as React from 'react';
import { Modal, ModalFooter, ModalTitle, ModalBody, ModalHeader } from '@common/components/modal';
import { I18n } from 'react-redux-i18n';
import cx from 'classnames';
import { FixedSizeList as List } from 'react-window';

import { Button } from '@common/components/Button';
import { Checkbox } from '@common/components/checkbox';
import { getTestAttributes } from '@common/utils/tests';
import { Ellipsis } from '@common/components/Ellipsis';
import { Flex } from '@common/components/Flex';

import { MODULE_NAME } from '../../database-security';

import './database-options.styles.scss';

const fields = {
  pedigreeNameUnique: 'pedigreeNameUnique',
  indNameUnique: 'indNameUnique',
};

export const DatabaseOptions = (props) => {
  const { className, value, getDuplicatedIndividuals, getDuplicatedPedigrees, onChange } = props;
  const [activeDuplicatedItemsSource, setActiveDuplicatedItemsSource] = React.useState('');
  const [duplicatedItems, setDuplicatedItems] = React.useState([]);
  const [isOpenDuplicatedItemsDialog, setOpenDuplicatedItemsDialogState] = React.useState(false);
  
  const renderSetting = React.useCallback((children) => {
    return (
      <div className="DatabaseOptions__setting">
        {children}
      </div>
    );
  }, []);

  const duplicatedItemsConfig = {
    [fields.pedigreeNameUnique]: {
      getDuplicatedItems: getDuplicatedPedigrees,
      title: I18n.t('Duplicate Pedigrees')
    },
    [fields.indNameUnique]: {
      getDuplicatedItems: getDuplicatedIndividuals,
      title: I18n.t('Duplicate Individuals')
    },
  };

  const columns = React.useMemo(() => [
    { name: 'Individual Name', fieldName: 'individualName', condition: activeDuplicatedItemsSource === fields.indNameUnique },
    { name: 'Pedigree Name', fieldName: 'pedigreeName', },
    { name: 'Folder Path', fieldName: 'folderPath' },
  ].filter(({ condition = true }) => condition).map((x) => ({ ...x, name: I18n.t(x.name) })), [activeDuplicatedItemsSource]);

  const handleChange = async (_, { e }) => {
    e.persist();

    const { checked, dataset } = e.target;
    
    if (checked) {
      const getItems = duplicatedItemsConfig[dataset.field].getDuplicatedItems;
      const duplicatedItems = await getItems();

      if (duplicatedItems.length) {
        setActiveDuplicatedItemsSource(dataset.field);
        setDuplicatedItems(duplicatedItems);
        setOpenDuplicatedItemsDialogState(true);

        return;
      }
    }

    onChange({
      ...value,
      [dataset.field]: checked,
    });
  };

  const closeDuplicatedItemsDialog = React.useCallback(() => {
    setOpenDuplicatedItemsDialogState(false);
  }, []);

  const cellWidth = `${100/columns.length}%`;

  const renderDuplicatedItem = React.useCallback(({ index, data, style }) => {
    const item = data[index];
    
    return (
      <Flex 
        justify="flex-start"
        alignItems="center"
        style={style}
        className="DatabaseOptions__duplicated-items__content__item"
        {...getTestAttributes(MODULE_NAME, 'duplicated-item', 'container', index)}
      >
        {columns.map((x) => (
          <Ellipsis 
            {...getTestAttributes(MODULE_NAME, 'duplicated-item', 'container', `${index}--${x.fieldName}--${item[x.fieldName]}`)} 
            width={cellWidth} 
            text={item[x.fieldName]} 
            key={x.fieldName} 
          />
        ))}
      </Flex>  
    );
  }, [columns]);

  const getDuplicatedItemKey = React.useCallback((index) => {
    return index;
  }, []);

  return (
    <>
      <section className={cx('DatabaseOptions', className)}>
        <h5 className="DatabaseOptions__title">{I18n.t('Database Options')}</h5>
        {renderSetting(
          <Checkbox 
            data-field={fields.pedigreeNameUnique}
            checked={value.pedigreeNameUnique} 
            className="UserSecuritySettings__checkbox" 
            {...getTestAttributes(MODULE_NAME, 'pedigree', 'checkbox')} 
            onChange={handleChange}
          >
            {I18n.t('Force Pedigree Name to be Unique')} 
          </Checkbox>
        )}
        {renderSetting(
          <Checkbox 
            data-field={fields.indNameUnique}
            checked={value.indNameUnique} 
            className="UserSecuritySettings__checkbox" 
            {...getTestAttributes(MODULE_NAME, 'individual', 'checkbox')} 
            onChange={handleChange}
          >
            {I18n.t('Force Individual Name to be Unique')} 
          </Checkbox>
        )}
      </section>
      <Modal
        show={isOpenDuplicatedItemsDialog}
        animation={false}
        className="DatabaseOptions__duplicated-items"
        backdrop="static"
        bsClass="bootstrap modal"
        dialogClassName="DatabaseOptions__duplicated-items"
        onEscapeKeyDown={closeDuplicatedItemsDialog}
        onHide={closeDuplicatedItemsDialog}
      >
        <ModalHeader closeButton onHide={closeDuplicatedItemsDialog}>
          <ModalTitle {...getTestAttributes(MODULE_NAME, 'database-options-modal', 'title')} >{activeDuplicatedItemsSource ? I18n.t(duplicatedItemsConfig[activeDuplicatedItemsSource].title) : undefined}</ModalTitle>
        </ModalHeader>
        <ModalBody className="DatabaseOptions__duplicated-items__content">
          <Flex width="100%" className="DatabaseOptions__duplicated-items__content__header"  justify="flex-start">
            {columns.map((x) => (
              <Ellipsis width={cellWidth} key={x.fieldName} text={x.name} />
            ))}
          </Flex>
          <List
            className="DatabaseOptions__duplicated-items__content__duplicated-items"
            height={300}
            itemCount={duplicatedItems.length}
            itemSize={26}
            width="100%"
            overscanCount={5}
            itemData={duplicatedItems}
            itemKey={getDuplicatedItemKey}
          >
            {renderDuplicatedItem}
          </List>
        </ModalBody>
        <ModalFooter>
          <Button bsStyle="primary" {...getTestAttributes(MODULE_NAME, 'database-options-modal', 'ok-btn')}  onClick={closeDuplicatedItemsDialog}>
            {I18n.t('Ok')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};